import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  markUnsuccessful,
  unmarkUnsuccessful,
} from "../services/mark-unsuccessful";

// Both endpoints write the session row before they respond, so invalidating is
// enough to show the change. These used to reload the whole page 2s after the
// toast, which threw away the operator's scroll position, filters, page number
// and open drawer for no gain.

export function useMarkUnsuccessful() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      source_id,
      reason,
    }: {
      source_id: string;
      reason: string;
    }) => markUnsuccessful(source_id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["sessions"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["user"], exact: false });
      queryClient.refetchQueries({ queryKey: ["user"], exact: false });
      toast({
        title: "Success",
        description: "Session marked as unsuccessful",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to mark session as unsuccessful",
        variant: "destructive",
      });
    },
  });
}

export function useUnmarkUnsuccessful() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ source_id }: { source_id: string }) =>
      unmarkUnsuccessful(source_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["sessions"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["user"], exact: false });
      queryClient.refetchQueries({ queryKey: ["user"], exact: false });
      toast({
        title: "Success",
        description: "Session reverted — no longer unsuccessful",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to revert session",
        variant: "destructive",
      });
    },
  });
}
